import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Video, CalendarClock, Lock, Sparkles } from "lucide-react";
import { BACKGROUND_PRESETS } from "@/lib/backgroundPresets";

/**
 * Meeting Defaults modal (v2.95 / v2.95b). Workspace-scoped defaults that
 * pre-fill create-time choices for NEWLY created meetings only — they never
 * touch existing meetings or any meeting engine (waiting room, room, lobby,
 * Agora). Editing is owner/admin gated (mirrors PATCH /workspaces/{id});
 * members get a read-only view.
 *
 * NOTE: transcription is deliberately NOT here — it has its own workspace
 * setting (transcription_auto_start) in Settings.jsx, which MeetingRoom.jsx
 * reads at runtime. Surfacing it here would duplicate/conflict with that.
 */

function ToggleRow({ label, description, checked, onChange, disabled, testid }) {
  return (
    <label className="flex items-start justify-between gap-4 cursor-pointer py-2">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <p className="text-xs text-[#8a8275] mt-0.5">{description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        data-testid={testid}
      />
    </label>
  );
}

export default function MeetingSettingsModal({ open, onOpenChange }) {
  const { currentWorkspace, fetchWorkspaces } = useWorkspace();
  const canEdit = ["owner", "admin"].includes(currentWorkspace?.my_role);

  const fxChip = (active) =>
    "px-2 py-2 text-xs text-center border transition-colors disabled:opacity-60 " +
    (active
      ? "bg-[#005e7a] border-[#005e7a] text-white"
      : "bg-white border-[#e5e0d6] text-[#4a4a52] hover:bg-[#f5f2ea]");

  // Instant defaults (fallbacks match current product behavior).
  const [iWaitingRoom, setIWaitingRoom] = useState(false);
  const [iGuestAccess, setIGuestAccess] = useState(true);
  const [iAiSummary, setIAiSummary] = useState(true);
  const [iAutoSummaryOnEnd, setIAutoSummaryOnEnd] = useState(true);
  // Scheduled defaults.
  const [sWaitingRoom, setSWaitingRoom] = useState(true);
  const [sGuestAccess, setSGuestAccess] = useState(true);
  const [sAiSummary, setSAiSummary] = useState(true);
  const [sAutoSummaryOnEnd, setSAutoSummaryOnEnd] = useState(true);

  // Video effect default (soft default; applies only to those who haven't
  // chosen their own effect — they can always override in the meeting).
  const [vBg, setVBg] = useState("none"); // "none" | "blur" | "preset"
  const [vBlur, setVBlur] = useState(2);
  const [vPreset, setVPreset] = useState(null);
  const [vBeauty, setVBeauty] = useState(false);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && currentWorkspace) {
      const w = currentWorkspace;
      setIWaitingRoom(w.instant_meeting_waiting_room_default ?? false);
      setIGuestAccess(w.instant_meeting_guest_access_default ?? true);
      setIAiSummary(w.instant_meeting_ai_summary_default ?? true);
      setIAutoSummaryOnEnd(w.instant_meeting_auto_summary_on_end_default ?? true);
      setSWaitingRoom(w.scheduled_meeting_waiting_room_default ?? true);
      setSGuestAccess(w.scheduled_meeting_guest_access_default ?? true);
      setSAiSummary(w.scheduled_meeting_ai_summary_default ?? true);
      setSAutoSummaryOnEnd(w.scheduled_meeting_auto_summary_on_end_default ?? true);
      setVBg(w.default_video_background || "none");
      setVBlur(w.default_video_blur_degree || 2);
      setVPreset(w.default_video_preset_id || null);
      setVBeauty(w.default_video_beauty_enabled ?? false);
    }
  }, [open, currentWorkspace]);

  const save = async () => {
    if (!currentWorkspace || !canEdit) return;
    setSaving(true);
    try {
      await api.patch(`/workspaces/${currentWorkspace.workspace_id}`, {
        instant_meeting_waiting_room_default: iWaitingRoom,
        instant_meeting_guest_access_default: iGuestAccess,
        instant_meeting_ai_summary_default: iAiSummary,
        instant_meeting_auto_summary_on_end_default: iAutoSummaryOnEnd,
        scheduled_meeting_waiting_room_default: sWaitingRoom,
        scheduled_meeting_guest_access_default: sGuestAccess,
        scheduled_meeting_ai_summary_default: sAiSummary,
        scheduled_meeting_auto_summary_on_end_default: sAutoSummaryOnEnd,
        default_video_background: vBg,
        default_video_blur_degree: vBlur,
        default_video_preset_id: vBg === "preset" ? vPreset : null,
        default_video_beauty_enabled: vBeauty,
      });
      await fetchWorkspaces();
      toast.success("Meeting defaults saved");
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not save meeting defaults");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[88vh] overflow-y-auto" data-testid="meeting-settings-modal">
        <DialogHeader>
          <DialogTitle>Meeting defaults</DialogTitle>
          <DialogDescription>
            Preconfigure how new meetings start in{" "}
            <span className="font-medium">{currentWorkspace?.name || "this workspace"}</span>.
            These apply to newly created meetings only — existing meetings are unchanged.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-1">
          {/* Instant Meeting Defaults */}
          <section className="border border-[#e5e0d6] p-4" data-testid="instant-defaults-card">
            <div className="flex items-center gap-2 mb-1">
              <Video className="w-4 h-4 text-[#8a8275]" />
              <h3 className="text-sm font-semibold">Instant Meeting Defaults</h3>
            </div>
            <div className="divide-y divide-[#efeae0]">
              <ToggleRow
                label="Waiting room"
                description="Participants wait until the host admits them."
                checked={iWaitingRoom}
                onChange={setIWaitingRoom}
                disabled={!canEdit}
                testid="instant-waiting-room-toggle"
              />
              <ToggleRow
                label="Guest access"
                description="People with the link can join without a Bloq account."
                checked={iGuestAccess}
                onChange={setIGuestAccess}
                disabled={!canEdit}
                testid="instant-guest-access-toggle"
              />
              <ToggleRow
                label="AI summary"
                description="Meeting is eligible for AI summary generation."
                checked={iAiSummary}
                onChange={setIAiSummary}
                disabled={!canEdit}
                testid="instant-ai-summary-toggle"
              />
              <ToggleRow
                label="Auto AI summary when meeting ends"
                description="Generate the AI summary automatically on end."
                checked={iAutoSummaryOnEnd}
                onChange={setIAutoSummaryOnEnd}
                disabled={!canEdit}
                testid="instant-auto-summary-toggle"
              />
            </div>
          </section>

          {/* Scheduled Meeting Defaults */}
          <section className="border border-[#e5e0d6] p-4" data-testid="scheduled-defaults-card">
            <div className="flex items-center gap-2 mb-1">
              <CalendarClock className="w-4 h-4 text-[#8a8275]" />
              <h3 className="text-sm font-semibold">Scheduled Meeting Defaults</h3>
            </div>
            <p className="text-xs text-[#8a8275] mb-1">Pre-fills the “Schedule meeting” form — you can still change each per meeting.</p>
            <div className="divide-y divide-[#efeae0]">
              <ToggleRow
                label="Waiting room"
                description="Participants wait until the host admits them."
                checked={sWaitingRoom}
                onChange={setSWaitingRoom}
                disabled={!canEdit}
                testid="scheduled-waiting-room-toggle"
              />
              <ToggleRow
                label="Guest access"
                description="People with the link can join without a Bloq account."
                checked={sGuestAccess}
                onChange={setSGuestAccess}
                disabled={!canEdit}
                testid="scheduled-guest-access-toggle"
              />
              <ToggleRow
                label="AI summary"
                description="Meeting is eligible for AI summary generation."
                checked={sAiSummary}
                onChange={setSAiSummary}
                disabled={!canEdit}
                testid="scheduled-ai-summary-toggle"
              />
              <ToggleRow
                label="Auto AI summary when meeting ends"
                description="Generate the AI summary automatically on end."
                checked={sAutoSummaryOnEnd}
                onChange={setSAutoSummaryOnEnd}
                disabled={!canEdit}
                testid="scheduled-auto-summary-toggle"
              />
            </div>
          </section>

          {/* Video Effect Default */}
          <section className="border border-[#e5e0d6] p-4" data-testid="video-effect-defaults-card">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-[#8a8275]" />
              <h3 className="text-sm font-semibold">Video Effect Default</h3>
            </div>
            <p className="text-xs text-[#8a8275] mb-3">
              Applies to anyone who hasn’t picked their own effect — members and guests.
              They can always change it in the meeting. Works best on desktop Chrome.
            </p>

            <div className="grid grid-cols-3 gap-2 mb-3">
              {[
                { key: "none", label: "None" },
                { key: "blur", label: "Blur" },
                { key: "preset", label: "Background" },
              ].map((o) => (
                <button
                  key={o.key}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => {
                    setVBg(o.key);
                    if (o.key === "preset" && !vPreset) setVPreset(BACKGROUND_PRESETS[0].id);
                  }}
                  className={fxChip(vBg === o.key)}
                  data-testid={`ws-video-bg-${o.key}`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {vBg === "blur" && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[{ k: 1, l: "Low" }, { k: 2, l: "Medium" }, { k: 3, l: "High" }].map((b) => (
                  <button
                    key={b.k}
                    type="button"
                    disabled={!canEdit}
                    onClick={() => setVBlur(b.k)}
                    className={fxChip(vBlur === b.k)}
                    data-testid={`ws-video-blur-${b.l.toLowerCase()}`}
                  >
                    {b.l}
                  </button>
                ))}
              </div>
            )}

            {vBg === "preset" && (
              <div className="grid grid-cols-3 gap-2 mb-3" data-testid="ws-video-presets">
                {BACKGROUND_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    disabled={!canEdit}
                    onClick={() => setVPreset(p.id)}
                    className={
                      "relative h-14 overflow-hidden border transition-colors " +
                      (vPreset === p.id
                        ? "border-[#005e7a] ring-2 ring-[#005e7a]"
                        : "border-[#e5e0d6] hover:border-[#c9c1b2]")
                    }
                    title={p.label}
                    data-testid={`ws-video-preset-${p.id}`}
                  >
                    <img src={p.src} alt={p.label} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="divide-y divide-[#efeae0]">
              <ToggleRow
                label="Touch up appearance"
                description="Subtle skin smoothing / brightness by default."
                checked={vBeauty}
                onChange={setVBeauty}
                disabled={!canEdit}
                testid="ws-video-beauty-toggle"
              />
            </div>
          </section>

          {!canEdit && (
            <p
              className="flex items-center gap-2 text-xs text-[#8a8275]"
              data-testid="meeting-settings-readonly-note"
            >
              <Lock className="w-3.5 h-3.5" />
              Only workspace owners or admins can change meeting defaults.
            </p>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="bloq-btn-secondary"
            data-testid="meeting-settings-cancel"
          >
            {canEdit ? "Cancel" : "Close"}
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="bloq-btn-primary disabled:opacity-60"
              data-testid="meeting-settings-save"
            >
              {saving ? "Saving…" : "Save defaults"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
